import { ADSettingsPaths } from "./Pages/AgentDesktop/paths";
import { AgentSettingsPaths } from "./Pages/AgentSettings/Pages/paths";
import { callBrandingPathResources } from "./Pages/CallBranding/Pages/CallBrandingPaths";
import { ComplianceSettingsPaths } from "./Pages/ComplianceSettings/paths";
import { SIPPaths } from "./Pages/sip/paths";
import { TrustPaths } from "./Pages/Trust/paths";
import { USER_MANAGEMENT_PATH, SettingsPaths } from "./paths";

/**
 * Settings metadata source of truth.
 *
 * Current behavior:
 * - Core title/description maps are used for Spotlight-aligned route/title matching.
 * - `_PATCH` values are temporary overrides for specific headers/sub-nav labels during migration.
 *
 * Direction:
 * - Unify all metadata behind product guidance so Spotlight, page headers, and sub-nav copy are consistent.
 */

export const SETTINGS_NAV_TITLES = {
    [SettingsPaths.generalSettings]: "General Settings",
    [USER_MANAGEMENT_PATH]: "User Management",
    [SettingsPaths.complianceSettings]: "Call Restrictions",
    [SettingsPaths.phoneNumbers]: "Active Phone Numbers",
    [SettingsPaths.externalTransferPhonebook]: "Transfer Destinations",
    [SettingsPaths.trust]: "Trust & Branding",
    [TrustPaths.trustOverview]: "Trust Overview",
    [TrustPaths.businessProfiles]: "Business Profiles",
    [TrustPaths.brandingAndSpam]: "Call Branding & Spam",
    [TrustPaths.stirShaken]: "STIR/SHAKEN",
    [TrustPaths.cnam]: "CNAM",
    [TrustPaths.a2p]: "A2P",
    [SettingsPaths.callBranding]: "Call Branding",
    [SettingsPaths.recordings]: "Recordings",
    [SettingsPaths.sip]: "SIP",
    [SettingsPaths.yourData]: "Data Management",
    [SettingsPaths.routingRules]: "Task Routing",
    [SettingsPaths.integrations]: "Integrations",
    [SettingsPaths.mySettings]: "My Settings",
    [SettingsPaths.agentDesktop]: "Agent Desktop",
    [SettingsPaths.contactLists]: "Contact Lists",
    [SettingsPaths.brandSecrets]: "Brand Secrets",
    [SettingsPaths.userManagementPageUsers]: "Users",
    [SettingsPaths.userManagementPageTeams]: "Teams",
    [SettingsPaths.userManagementPageSkills]: "Skills",
    [SettingsPaths.userManagementPageCustomAttributes]: "Custom Attributes",
    [ComplianceSettingsPaths.contactRestrictions]: "Daily Available Hours",
    [ComplianceSettingsPaths.holidays]: "Holidays",
    [ComplianceSettingsPaths.callLimits]: "Daily Call Limits",
    [ComplianceSettingsPaths.stateOfEmergency]: "State of Emergency",
    [SIPPaths.sipDomains]: "SIP Domains",
    [SIPPaths.ipAccessControlLists]: "IP Access Control Lists",
    [SettingsPaths.objectTypes]: "Objects",
    [SettingsPaths.eventManagement]: "Events",
    [ADSettingsPaths.taskPreferences]: "Task Preferences",
    [ADSettingsPaths.dispositions]: "Dispositions",
    [ADSettingsPaths.tags]: "Tags",
    [ADSettingsPaths.cancellationReasons]: "Cancellation Reasons",
    [ADSettingsPaths.templates]: "Templates",
    [ADSettingsPaths.activityStatuses]: "Activity Statuses",
    [ADSettingsPaths.agentPermissions]: "Agent Desktop Permissions",
    [AgentSettingsPaths.integrations]: "Email Integrations",
    [AgentSettingsPaths.recordings]: "My Pre-recorded Voicemail Drops",
} as const;

export const SETTINGS_PAGE_DESCRIPTIONS = {
    [SettingsPaths.generalSettings]: "System-wide configuration and settings management.",
    [SettingsPaths.userManagementPageUsers]: "User account and permission management.",
    [SettingsPaths.userManagementPageTeams]: "Team management and configuration interface.",
    [SettingsPaths.userManagementPageSkills]: "Skill management and configuration.",
    [SettingsPaths.userManagementPageCustomAttributes]:
        "Create and manage custom user attributes. Max 10 attributes allowed.",
    [SettingsPaths.complianceSettings]: "Configure call restrictions and compliance guardrails",
    [ComplianceSettingsPaths.contactRestrictions]:
        "Configure daily available hours and contact restrictions for compliance.",
    [ComplianceSettingsPaths.holidays]: "Manage holiday schedules and contact restrictions.",
    [ComplianceSettingsPaths.callLimits]: "Configure daily call limits for compliance.",
    [ComplianceSettingsPaths.stateOfEmergency]: "Manage state of emergency contact restrictions.",
    [SettingsPaths.phoneNumbers]: "Phone system configuration and management.",
    [SettingsPaths.externalTransferPhonebook]:
        "Configure the destinations agents can use for transfers and external handoffs.",
    [SettingsPaths.trust]: "Configure branding with available trust products to improve customer engagement.",
    [TrustPaths.trustOverview]:
        "Improve customer engagement and brand recognition with Regal's suite of trust products that are designed to work together to break through the noise.",
    [TrustPaths.businessProfiles]:
        "Business profile management for phone number verification and trust product configuration.",
    [TrustPaths.brandingAndSpam]:
        "Avoid spam flags and display your brand name on customers' mobile phones. Covers branded caller ID and spam remediation.",
    [SettingsPaths.callBranding]: "Management of branded phone numbers with custom caller ID features.",
    [SettingsPaths.recordings]: "Call recording management and configuration.",
    [SettingsPaths.sip]: "View and manage your SIP domains for routing SIP traffic through Twilio.",
    [SIPPaths.sipDomains]: "SIP domain management and configuration for voice communications.",
    [SIPPaths.ipAccessControlLists]: "Manage IP access control lists for SIP security.",
    [SettingsPaths.contactLists]: "Contact list management and configuration.",
    [SettingsPaths.integrations]: "Third-party integration management and configuration.",
    [ADSettingsPaths.taskPreferences]: "Task handling preferences and configuration.",
    [ADSettingsPaths.dispositions]: "Call disposition management and configuration.",
    [ADSettingsPaths.tags]: "Tag management for categorizing and organizing contacts.",
    [ADSettingsPaths.cancellationReasons]: "Management of predefined cancellation reasons.",
    [ADSettingsPaths.templates]: "Create and manage pre-canned SMS templates.",
    [ADSettingsPaths.activityStatuses]: "Configure activity statuses for agent availability and scheduling.",
    [ADSettingsPaths.agentPermissions]: "Configure permissions and access control for agent desktop features.",
    [AgentSettingsPaths.integrations]: "Manage email integrations for your agent.",
    [AgentSettingsPaths.recordings]: "Manage your personal pre-recorded voicemail drops.",
} as const;

// Header and nav title overrides used by page headers/sub-nav during migration.
export const SETTINGS_NAV_TITLES_PATCH = {
    [`${ADSettingsPaths.templates}/new`]: "Set Up New Template",
    [`${SettingsPaths.objectTypes}/new`]: "Create New Object",
    [`${SIPPaths.sipDomains}/new`]: "Create SIP Domain",
    [SIPPaths.sipDomainDetails]: "SIP Domain Details",
    [`${SettingsPaths.yourData}/contact-profile`]: "Edit 'Contact Profile'",
    [callBrandingPathResources.phoneNumbers]: "Branded Caller ID",
    [AgentSettingsPaths.integrations]: "Integrations",
    [SettingsPaths.contactLists]: "Contact Ownership",
    [SettingsPaths.userManagementPageUsers]: "Users",
    [SettingsPaths.integrations]: "Integrations",
    [AgentSettingsPaths.recordings]: "My Pre-recorded Voicemail Drops",

    [SettingsPaths.yourData]: "Your Data",
} as const;

// Description/subtitle overrides used where page body copy differs from base metadata.
export const SETTINGS_NAV_DESCRIPTIONS_PATCH = {
    [SettingsPaths.phoneNumbers]: "Manage all of your active Regal phone numbers.",
    [SettingsPaths.externalTransferPhonebook]:
        "Configure the destinations agents can use for transfers and external handoffs.",
    [ADSettingsPaths.templates]:
        "Create and manage pre-canned SMS templates. Agents can select templates from the Agent Desktop to respond faster and more consistently.",
    [SIPPaths.ipAccessControlLists]:
        "Manage IP Access Control Lists to restrict which IP addresses can connect to your SIP domains.",
    [ComplianceSettingsPaths.holidays]: "State-specific or Federal holidays on which calls are blocked.",
    [ComplianceSettingsPaths.callLimits]: "The maximum number of calls that can be placed to a contact each day.",
    [SettingsPaths.eventManagement]:
        "Manage events from all sources, including events generated by the Regal App. Determine how your events appear and function within your account.",
    [ComplianceSettingsPaths.contactRestrictions]:
        "The daily time windows or days of the week when contacts can be contacted by phone.",
    [ComplianceSettingsPaths.stateOfEmergency]: "Emergencies (eg. natural disasters) during which calls are blocked.",
    [ADSettingsPaths.cancellationReasons]:
        "Manage the list of cancellation reasons agents can select when canceling tasks.",
    [ADSettingsPaths.taskPreferences]: "Set up task preferences like Task Auto-Complete by clicking on a task type.",
    [ADSettingsPaths.activityStatuses]: "Manage the Activity Statuses available to agents.",
    [ADSettingsPaths.tags]: "Manage the list of tags agents can use when summarizing tasks.",
    [ADSettingsPaths.dispositions]: "Manage the list of dispositions that agents can use to summarize tasks.",
    [SettingsPaths.brandSecrets]: "Manage API keys and credentials used by your brand's workflows and integrations.",
    [SettingsPaths.userManagementPageUsers]: "Create and manage users.",
    [SettingsPaths.recordings]: "Manage all recordings for voicemails and IVR in your account.",
    [AgentSettingsPaths.recordings]:
        "Manage your voicemail drop recordings for outbound calls. Set up default voicemail and campaign association for quicker voicemail drop.",
} as const;

export const SETTINGS_PAGE_CONFIG_PATCH = {
    [SettingsPaths.sip]: "SIP Settings",
} as const;
